import { useCallback, useEffect, useState } from "react";
import { useCurrentStateAndParams } from "@uirouter/react";

import { useDispatch } from "react-redux";
import styled from "styled-components";

import Sentry from "@similarweb/sentry-logger";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import type { SubTitleAlignment } from "@similarweb/ui-components";
import { UseCaseHomepage, SubTitleWrapper } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { showSuccessToast } from "app/actions/toast_actions";
import { DeleteKeywordListConfirmationModal } from "app/components/modals/components/DeleteKeywordListConfirmationModal";
import { swSettings } from "app/common/services/swSettings";
import { ScrollContainer } from "app/components/layout/ScrollContainer";
import { getCountries } from "app/components/filters-bar/utils";
import { i18nFilter } from "app/filters/i18nFilter";
import { getQueryKey } from "app/modules/api-client";
import { createKeywordGroupEntity } from "app/modules/demand-analysis/keyword-lists";
import { KeywordsGroupEditorModal } from "app/pages/keyword-analysis/KeywordsGroupEditorModal";
import { useKeywordGroupModalWorkflow } from "app/pages/keyword-analysis/useKeywordGroupModalWorkflow";
import { KeywordGroupsShareModal } from "app/pages/workspace/marketing/pages/KeywordGroupShareModal";
import type { ICountryObject } from "app/services/CountryService";
import { AssetsService } from "app/services/AssetsService";
import { keywordGroupsTransforms, useAllKeywordGroups } from "app/services/keyword-groups";
import type { IUserKeywordGroup } from "app/services/keyword-groups/keywordGroups.types";
import { allTrackers } from "app/services/track/track";
import TranslationProvider from "pro-features/components/WithTranslation/src/TranslationProvider";
import { KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE } from "pro-features/pages/module start page/src/keyword analysis/consts";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import queryClient from "single-spa/react-app/query-client";

import StartPageCountryFilter from "./components/StartPageCountryFilter";
import { DemandAnalysisHomeSearch, type KeywordListComponentMode } from "./features/demand-analysis-home-search";
import { TrackSection } from "./features/demand-analysis-home-track-section/TrackSection";
import { TopicsDiscover } from "./features/topics-discover/TopicsDiscover";
import { handleNonKeywordGroupItemClick } from "./demandAnalysisKeywordItemClick";
import { resolveAllowedCountry } from "./shared/countryResolution";
import type { IDemandAnalysisHomePageParams } from "./types";

const HEADER_IMAGE_URL = AssetsService.assetUrl("/images/secondary-home-page-header-light.png");

const UseCaseHomepageWrapper = styled.div`
    ${SubTitleWrapper} > span {
        ${setFontVariant("body2", colorsPalettes.carbon[500])};
    }
`;

const DEFAULT_PAGE_STATE = "organicsearch_keywordAnalysis_overview_2";

enum ETabIndex {
    MY_LISTS = 0,
    SHARED_LISTS = 1,
}

interface ISaveKeywordGroupProps {
    targetStateOnSave: string;
    showToast: boolean;
}

const DEFAULT_SAVE_KEYWORD_GROUPS_PROPS: ISaveKeywordGroupProps = {
    targetStateOnSave: DEFAULT_PAGE_STATE,
    showToast: true,
};

const invalidateDemandSearchTrendsQueries = () => {
    queryClient.removeQueries({
        queryKey: getQueryKey({
            tag: "DemandSearchTrends",
            methodName: "marketResearchDemandAnalysisSearchTrendsList",
        }),
    });
};

interface IDemandAnalysisHomePageProps {
    pageState?: string;
    subtitlePosition?: SubTitleAlignment;
    title: string;
    subtitle?: string;
    ctaButtonText?: string;
    saveKeywordGroupProps?: ISaveKeywordGroupProps;
    supportCountryFilters?: boolean;
    textContent?: {
        placeholderText?: string;
        placeholderBoldText?: string;
        keywordsTabTitle?: string;
        keywordGroupsTabTitle?: string;
        topicsTabTitle?: string;
        aiTopicsTabTitle?: string;
    };
    showAutoCreateKeywordGroupButton?: boolean;
    useAutogeneratedKeywordGroupVersion2?: boolean;
    mode?: KeywordListComponentMode;
    onItemClickCustom?: (item: any) => void;
}

export const DemandAnalysisHomePage = ({
    pageState = DEFAULT_PAGE_STATE,
    subtitlePosition = "left-aligned",
    title,
    subtitle,
    ctaButtonText = "marketintelligence.marketresearch.keywordmarket.cta.button",
    saveKeywordGroupProps = DEFAULT_SAVE_KEYWORD_GROUPS_PROPS,
    supportCountryFilters = false,
    textContent = {
        placeholderText: "marketintelligence.marketresearch.keywordmarket.home.searchText",
        placeholderBoldText: "marketintelligence.marketresearch.keywordmarket.home.searchItem",
    },
    showAutoCreateKeywordGroupButton = true,
    useAutogeneratedKeywordGroupVersion2 = false,
    onItemClickCustom = undefined,
    mode = "keywordLists",
}: IDemandAnalysisHomePageProps) => {
    const { params } = useCurrentStateAndParams<IDemandAnalysisHomePageParams>();
    const [selectedTab, setSelectedTab] = useState<ETabIndex>(ETabIndex.MY_LISTS);
    const [keywordGroups, setKeywordGroups] = useState([]);
    const [sharedKeywordGroups, setSharedKeywordGroups] = useState([]);
    const [keywordGroupToShare, setKeywordGroupToShare] = useState<IUserKeywordGroup>();
    const availableCountries = getCountries();
    const [selectedCountry, setSelectedCountry] = useState<ICountryObject>(availableCountries[0]);
    const dispatch = useDispatch();
    const i18n = i18nFilter();
    const topCountries = getCountries();
    const { userGroups, sharedGroups } = useAllKeywordGroups();
    const { controller, handleBeforeDelete, requestDelete, deleteConfirmationModalProps } =
        useKeywordGroupModalWorkflow();

    const sortGroups = (a, b) => a.Name.toLowerCase().localeCompare(b.Name.toLowerCase());

    useEffect(() => {
        setKeywordGroups(userGroups.sort(sortGroups));
        setSharedKeywordGroups(sharedGroups.sort(sortGroups));
    }, [userGroups, sharedGroups]);

    const homeCountryId =
        params.country ??
        (supportCountryFilters && selectedCountry ? selectedCountry : (topCountries[0] as ICountryObject)).id;

    const replaceGroup = (currentGroupIndex: number, modifiedGroup: IUserKeywordGroup) => {
        const firstHalfWithoutGroup = keywordGroups.slice(0, currentGroupIndex);
        const secondHalfWithoutGroup = keywordGroups.slice(currentGroupIndex + 1);
        setKeywordGroups([...firstHalfWithoutGroup, modifiedGroup, ...secondHalfWithoutGroup]);
    };

    const mergeNewGroupIntoList = (newGroup: IUserKeywordGroup) => {
        const groupsSortedByName = keywordGroups.concat(newGroup).sort(sortGroups);
        setKeywordGroups(groupsSortedByName);
    };

    const getDefaultParams = () => {
        const state = proNavigator.getState(pageState);
        const configId = proNavigator.getConfigId(state);
        return swSettings.components[configId].defaultParams;
    };

    const onItemClick = (item, ignoreFilterConsistency = false) => {
        const defaultParams = getDefaultParams();
        proNavigator.go(pageState, {
            ...defaultParams,
            keyword: item.Id ? `*${item.Id}` : item.name,
            webSource: "Total",
            country: homeCountryId,
            isTerm: false,
            ignoreFilterConsistency,
        });
    };

    const handleSave = (modifiedOrNewGroup: IUserKeywordGroup) => {
        const { targetStateOnSave, showToast } = saveKeywordGroupProps;
        const defaultParams = getDefaultParams();

        const index = keywordGroups.findIndex((kw) => kw.Id === modifiedOrNewGroup.Id);
        // the group existed and was edited
        if (index !== -1) {
            replaceGroup(index, modifiedOrNewGroup);
        } else {
            // a new group was created
            mergeNewGroupIntoList(modifiedOrNewGroup);
        }

        try {
            const resolvedCountry = resolveAllowedCountry(
                KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE,
                homeCountryId,
            );

            const keywordListEntity = createKeywordGroupEntity(
                { type: "KeywordGroup", keywordGroupId: modifiedOrNewGroup.Id },
                modifiedOrNewGroup.Name,
            );

            void proNavigator.go(KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE, {
                ...defaultParams,
                id: [keywordListEntity.keywordListId],
                country: resolvedCountry,
                ignoreFilterConsistency: true,
            });
        } catch (error) {
            Sentry.error(
                "[KeywordAnalysisStartPageContainer] Failed to navigate after saving keyword group",
                error as Error,
                { tags: { team: "ants" } },
            );
        }

        if (showToast) {
            dispatch(showSuccessToast(i18n("create.list.wizard.toast", { listName: modifiedOrNewGroup.Name })));
        }
    };

    const handleEditClick = (item) => {
        if (selectedTab === ETabIndex.MY_LISTS) {
            controller.openSession({ keywordsGroup: item });
            allTrackers.dispatchTrackEvent("Button", "click", "Edit Keyword Group/Edit from KWA homepage");
        }
    };

    const handleShareClick = useCallback(
        (item: IUserKeywordGroup) => {
            const allGroups = [...keywordGroups, ...sharedKeywordGroups];
            const transformedGroupDataForSharing = keywordGroupsTransforms.adaptGroupDataForShareModal(item, allGroups);
            setKeywordGroupToShare(transformedGroupDataForSharing);
            allTrackers.dispatchTrackEvent("Drop Down", "Click", "Share/Share icon");
        },
        [keywordGroups, sharedKeywordGroups],
    );

    const handleFinishSharing = () => {
        setKeywordGroupToShare(undefined);
    };

    const handleDeleteClick = (item: IUserKeywordGroup) => {
        requestDelete(item);
        allTrackers.dispatchTrackEvent("Drop Down", "Click", "Delete");
    };

    const itemClickEventHandler = (
        name: string,
        id: string,
        isSharedWithMe: string,
        numKeywords: string,
        eventName: string,
    ) => {
        allTrackers.dispatchTrackEvent("Select keyword list", "click", eventName, {
            target_KW_list_type: "Keyword list",
            target_KW_list_name: name,
            target_KW_list_ID: id,
            target_KW_list_shared_with_me: isSharedWithMe,
            target_KW_list_num_keywords: numKeywords,
        });
    };

    const handleCreateKeywordGroupButtonClick = (item: any) => () => {
        const isKeywordGroup = item.hasOwnProperty("Id");
        if (isKeywordGroup) {
            const { Name, Id, Keywords, SharedWithMe } = item;
            itemClickEventHandler(Name, Id, SharedWithMe, Keywords?.length, "Auto complete");
            onItemClick(item, true);
        } else {
            handleNonKeywordGroupItemClick(item, {
                onItemClickCustom,
                supportCountryFilters,
                onCountryFilterClick: (keyword) => controller.openSession({ seedKeyword: keyword }),
                onDefaultClick: () => onItemClick(item),
            });
        }
    };

    const handleSuccessToastLinkClick = (keywordGroupId: string) => {
        try {
            const state = proNavigator.getState(KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE);
            const resolvedCountry = resolveAllowedCountry(
                KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE,
                homeCountryId,
            );

            const keywordListEntity = createKeywordGroupEntity(
                { type: "KeywordGroup", keywordGroupId },
                keywordGroupId,
            );

            void proNavigator.go(KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE, {
                ...state.defaultQueryParams,
                id: [keywordListEntity.keywordListId],
                country: resolvedCountry,
            });
        } catch (error) {
            Sentry.error("[KeywordAnalysisStartPageContainer] Failed to navigate to keyword group", error as Error, {
                tags: { team: "ants" },
            });
        }
    };

    return (
        <TranslationProvider translate={i18n}>
            <ScrollContainer>
                <UseCaseHomepageWrapper>
                    <UseCaseHomepage
                        title={i18n(title)}
                        titleFontSize="34px"
                        titlePosition="left-aligned"
                        subtitle={i18n(subtitle)}
                        paddingTop="80px"
                        paddingBottom="80px"
                        headerImageUrl={HEADER_IMAGE_URL}
                        subtitlePosition={subtitlePosition}
                        searchComponents={
                            <DemandAnalysisHomeSearch
                                mode={mode}
                                textContent={{
                                    ...textContent,
                                    keywordsTabTitle: textContent.keywordsTabTitle,
                                    keywordGroupsTabTitle: textContent.keywordGroupsTabTitle,
                                    topicsTabTitle: textContent.topicsTabTitle,
                                    aiTopicsTabTitle: textContent.aiTopicsTabTitle,
                                }}
                                countryFilter={
                                    supportCountryFilters && mode !== "full" ? (
                                        <StartPageCountryFilter
                                            selectedCountry={selectedCountry}
                                            onChangeSelectedCountry={setSelectedCountry}
                                            availableCountries={availableCountries}
                                        />
                                    ) : undefined
                                }
                                supportCountryFilters={supportCountryFilters}
                                showAutoCreateKeywordGroupButton={showAutoCreateKeywordGroupButton}
                                handleSave={handleSave}
                                handleItemClick={handleCreateKeywordGroupButtonClick}
                                handleKeywordGroupButtonClick={
                                    useAutogeneratedKeywordGroupVersion2
                                        ? handleCreateKeywordGroupButtonClick
                                        : undefined
                                }
                            />
                        }
                        listItems={
                            <Flex
                                flexDirection="column"
                                gap="40px"
                                padding="0 0 40px"
                            >
                                <TopicsDiscover />
                                <TrackSection
                                    countryId={homeCountryId}
                                    callToActionButtonMessage={ctaButtonText}
                                    selectedTabIndex={selectedTab}
                                    onTabChange={(index) => setSelectedTab(index)}
                                    onEntityEditClick={handleEditClick}
                                    onEntityShareClick={handleShareClick}
                                    onEntityDeleteClick={handleDeleteClick}
                                />
                            </Flex>
                        }
                    />
                </UseCaseHomepageWrapper>
            </ScrollContainer>
            <KeywordsGroupEditorModal
                showGoToMonitorKeywordListButton
                controller={controller}
                editTargetState={KEYWORD_MARKET_ANALYSIS_GROUP_SEARCH_TRENDS_STATE}
                onSave={handleSave}
                onEdit={invalidateDemandSearchTrendsQueries}
                onBeforeDelete={handleBeforeDelete}
                onClose={() => undefined}
                onSuccessToastLinkClick={handleSuccessToastLinkClick}
            />
            {keywordGroupToShare ? (
                <KeywordGroupsShareModal
                    keywordGroup={keywordGroupToShare}
                    isOpen={Boolean(keywordGroupToShare)}
                    onCloseClick={handleFinishSharing}
                    onFinish={handleFinishSharing}
                />
            ) : null}
            <DeleteKeywordListConfirmationModal
                {...deleteConfirmationModalProps}
                dataAutomationPrefix="demand-analysis-home-delete-keyword-list-confirmation"
            />
        </TranslationProvider>
    );
};
